/**
 * Post-match feedback questionnaire definition.
 *
 * The form renders by iterating over this array and dispatching on `type`.
 * Adding a question is a config change here + i18n keys in en/fr/de.
 *
 * Discriminated union: TS will force exhaustive handling in the renderer.
 */

type ScaleQuestion = {
  type: 'scale';
  id: string;
  labelKey: string;
  /** Visual variant: 'demand' uses gauge icons (Q1), 'feel' uses faces (Q2). */
  variant: 'demand' | 'feel';
  options: { value: number; labelKey: string }[];
  /**
   * Optional follow-up shown only when the picked level is >= `triggerFrom`.
   * Single-select chips that qualify *why* the level is that high. The answer
   * becomes `{ value, factor }`; `factor` is omitted when nothing is picked.
   */
  refine?: {
    /** Minimum scale value that reveals the chips (e.g. 4 = the two rightmost). */
    triggerFrom: number;
    labelKey: string;
    options: { value: string; labelKey: string }[];
  };
};

type ChipsQuestion = {
  type: 'chips';
  id: string;
  labelKey: string;
  /**
   * One or more chip groups. Each group is independent single-select.
   * The submitted value is `{ [groupId]: selectedValue | null }`.
   */
  groups: {
    id: string;
    labelKey: string;
    options: { value: string; labelKey: string }[];
  }[];
};

type TaxonomyQuestion = {
  type: 'taxonomy';
  id: string;
  labelKey: string;
  hintKey?: string;
  notePlaceholderKey?: string;
  /**
   * Two-level: pick a category, then a sub-item. Single-select across
   * the whole taxonomy. The submitted value is
   * `{ category, subItem, note }` or `null`.
   */
  categories: {
    id: string;
    labelKey: string;
    options: { value: string; labelKey: string }[];
  }[];
};

export type FeedbackQuestion = ScaleQuestion | ChipsQuestion | TaxonomyQuestion;

const I18N_BASE = 'screens.feedback.questions';

export const feedbackQuestions: FeedbackQuestion[] = [
  {
    type: 'scale',
    id: 'demand',
    variant: 'demand',
    labelKey: `${I18N_BASE}.demand.label`,
    options: [
      { value: 1, labelKey: `${I18N_BASE}.demand.options.1` },
      { value: 2, labelKey: `${I18N_BASE}.demand.options.2` },
      { value: 3, labelKey: `${I18N_BASE}.demand.options.3` },
      { value: 4, labelKey: `${I18N_BASE}.demand.options.4` },
      { value: 5, labelKey: `${I18N_BASE}.demand.options.5` },
    ],
    refine: {
      triggerFrom: 4,
      labelKey: `${I18N_BASE}.demand.refine.label`,
      options: [
        { value: 'longMatch', labelKey: `${I18N_BASE}.demand.refine.options.longMatch` },
        { value: 'fastPace', labelKey: `${I18N_BASE}.demand.refine.options.fastPace` },
        { value: 'toughCalls', labelKey: `${I18N_BASE}.demand.refine.options.toughCalls` },
      ],
    },
  },
  {
    type: 'scale',
    id: 'feel',
    variant: 'feel',
    labelKey: `${I18N_BASE}.feel.label`,
    options: [
      { value: 1, labelKey: `${I18N_BASE}.feel.options.1` },
      { value: 2, labelKey: `${I18N_BASE}.feel.options.2` },
      { value: 3, labelKey: `${I18N_BASE}.feel.options.3` },
      { value: 4, labelKey: `${I18N_BASE}.feel.options.4` },
      { value: 5, labelKey: `${I18N_BASE}.feel.options.5` },
    ],
  },
  {
    type: 'chips',
    id: 'stoodOut',
    labelKey: `${I18N_BASE}.stoodOut.label`,
    groups: [
      {
        id: 'climate',
        labelKey: `${I18N_BASE}.stoodOut.groups.climate.label`,
        options: [
          { value: 'serene', labelKey: `${I18N_BASE}.stoodOut.groups.climate.options.serene` },
          { value: 'tension', labelKey: `${I18N_BASE}.stoodOut.groups.climate.options.tension` },
          { value: 'contested', labelKey: `${I18N_BASE}.stoodOut.groups.climate.options.contested` },
        ],
      },
      {
        id: 'audience',
        labelKey: `${I18N_BASE}.stoodOut.groups.audience.label`,
        options: [
          { value: 'none', labelKey: `${I18N_BASE}.stoodOut.groups.audience.options.none` },
          { value: 'fine', labelKey: `${I18N_BASE}.stoodOut.groups.audience.options.fine` },
          { value: 'pressure', labelKey: `${I18N_BASE}.stoodOut.groups.audience.options.pressure` },
        ],
      },
    ],
  },
  {
    type: 'taxonomy',
    id: 'improvement',
    labelKey: `${I18N_BASE}.improvement.label`,
    hintKey: `${I18N_BASE}.improvement.hint`,
    notePlaceholderKey: `${I18N_BASE}.improvement.notePlaceholder`,
    categories: [
      {
        id: 'technical',
        labelKey: `${I18N_BASE}.improvement.categories.technical.label`,
        options: [
          { value: 'announcements', labelKey: `${I18N_BASE}.improvement.categories.technical.options.announcements` },
          { value: 'position', labelKey: `${I18N_BASE}.improvement.categories.technical.options.position` },
          { value: 'decisions', labelKey: `${I18N_BASE}.improvement.categories.technical.options.decisions` },
        ],
      },
      {
        id: 'mental',
        labelKey: `${I18N_BASE}.improvement.categories.mental.label`,
        options: [
          { value: 'confidence', labelKey: `${I18N_BASE}.improvement.categories.mental.options.confidence` },
          { value: 'focus', labelKey: `${I18N_BASE}.improvement.categories.mental.options.focus` },
          { value: 'tension', labelKey: `${I18N_BASE}.improvement.categories.mental.options.tension` },
          { value: 'timing', labelKey: `${I18N_BASE}.improvement.categories.mental.options.timing` },
        ],
      },
      {
        id: 'rules',
        labelKey: `${I18N_BASE}.improvement.categories.rules.label`,
        options: [
          { value: 'interference', labelKey: `${I18N_BASE}.improvement.categories.rules.options.interference` },
          { value: 'conduct', labelKey: `${I18N_BASE}.improvement.categories.rules.options.conduct` },
          { value: 'timing', labelKey: `${I18N_BASE}.improvement.categories.rules.options.timing` },
          { value: 'service', labelKey: `${I18N_BASE}.improvement.categories.rules.options.service` },
          { value: 'ballHitsPlayer', labelKey: `${I18N_BASE}.improvement.categories.rules.options.ballHitsPlayer` },
          { value: 'injuries', labelKey: `${I18N_BASE}.improvement.categories.rules.options.injuries` },
        ],
      },
    ],
  },
];
